import { AfterViewInit, Component, OnInit, ViewChild } from "@angular/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { LocaleService } from "../../../shared/services/locale.service";
import { Observable, of } from "rxjs";
import { catchError, debounceTime, map } from "rxjs/operators";
import { empty } from "../../../functions";
import { StudentProfileService } from "../../../api/services/student-profile.service";
import {
  alertMessages,
  AlertService,
} from "../../../shared/services/alert.service";
import { ValidationService } from "../../../shared/services/validation.service";
import { LanguageProficiencyUtils } from "../../../shared/utils/student-profile.utils";
import { LanguageProficiency } from "src/app/api/models";

@Component({
  selector: "app-modal-student-profile-language",
  templateUrl: "./modal-student-profile-language.component.html",
  standalone: false,
})
export class ModalStudentProfileLanguageComponent
  implements OnInit, AfterViewInit
{
  @ViewChild("languageTypeaheadInput") languageTypeaheadInput: any;

  // Data container for modal (contains entity data)
  public data: any;

  public profile_id = "";

  /**
   * Entity
   */
  entity: LanguageProficiency = {
    id: "",
    languageCode: "",
    speakingProficiency: 0,
    readingProficiency: 0,
    writingProficiency: 0,
  };

  validation: any = {
    validated: false,
    valid: false,
    fields: {
      entity: {
        languageCode: {
          regex: "required",
          error: false,
        },
      },
    },
  };

  /**
   * Language levels
   */
  languageLevels: any[] = [];

  public language_typeahead: any[] = [];

  languageTypeahead = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(200),
      map((term) =>
        term === ""
          ? []
          : this.language_typeahead
              .filter(
                (v) =>
                  (
                    v.nativeName.toLowerCase() +
                    " " +
                    v.name.toLowerCase()
                  ).indexOf(term.toLowerCase()) > -1
              )
              .slice(0, 10)
      )
    );

  formatter = (x: any) => x.name || x.nativeName;

  /**
   * Constructor
   *
   * @param activeModal
   */
  constructor(
    public activeModal: NgbActiveModal,
    public locale: LocaleService,
    private studentProfileService: StudentProfileService,
    private alertService: AlertService,
    private validationService: ValidationService
  ) {
    this.language_typeahead = this.locale.availableLanguages();

    this.languageLevels = LanguageProficiencyUtils.getLevels();
  }

  /**
   * Ng init
   */
  ngOnInit(): void {
    if (!empty(this.data)) {
      this.profile_id = this.data.profile_id;
      this.entity = this.data.languageProficiency;
    }
  }

  /**
   * Ng after view init
   */
  ngAfterViewInit() {
    if (!empty(this.entity.languageCode)) {
      this.locale.getLanguageTypeahead().subscribe((data) => {
        const language = data.find(
          (lang) => lang.code === this.entity.languageCode
        );
        if (language) {
          this.languageTypeaheadInput.nativeElement.value =
            language.name || language.nativeName;
        }
      });
    }
  }

  /**
   * Set selected language code
   *
   * @param $event
   */
  setLanguage($event: any) {
    if ($event.hasOwnProperty("item")) {
      this.entity.languageCode = $event.item.code;
    }
  }

  /**
   * Show validation error
   *
   * Determine if a validation error needs to be visible to client
   *
   * @param stepNumber
   * @param $entity
   * @param $entityProperty
   */
  showValidationError(
    $entity: string,
    $entityProperty: string,
    $property: string = ""
  ) {
    if (!empty($property)) {
      if (
        this.validation["validated"] &&
        this.validation["validated"]["fields"][$entity][$entityProperty][
          $property
        ]["error"] === true
      ) {
        return true;
      } else {
        return false;
      }
    } else {
      if (
        this.validation["validated"] &&
        this.validation["fields"][$entity][$entityProperty]["error"] === true
      ) {
        return true;
      } else {
        return false;
      }
    }
  }

  /**
   * Validate the registration form
   */
  validateForm() {
    let valid = true;
    const self: any = this;

    // Set entity data selectors
    const $entitySelectors = ["entity"];

    // Check entity columns
    for (const $entitySelector of $entitySelectors) {
      if (this.validation["fields"].hasOwnProperty($entitySelector)) {
        for (const key in this.validation["fields"][$entitySelector]) {
          if (!empty(this.validation["fields"][$entitySelector][key])) {
            const entity = this.validation["fields"][$entitySelector][key];

            if (entity.hasOwnProperty("regex")) {
              const value = self[$entitySelector][key];
              const regPattern = entity["regex"];

              // Set validation flags
              if (!this.validationService.regexValidation(regPattern, value)) {
                this.validation["fields"][$entitySelector][key]["error"] = true;
                valid = false;
              } else {
                this.validation["fields"][$entitySelector][key]["error"] =
                  false;
              }
            }
          }
        }
      }
    }

    // Set validation flags for step
    this.validation["valid"] = valid;
    this.validation["validated"] = true;

    return valid;
  }

  /**
   * Save data
   *
   * @constructor
   */
  Save() {
    const valid = this.validateForm();

    if (valid) {
      if (!empty(this.entity.id)) {
        this.studentProfileService
          .updateLanguageProficiency({
            profileId: this.profile_id,
            id: this.entity.id!,
            body: this.entity,
          })
          .pipe(
            catchError((err) => {
              this.alertService.errorWithReport(
                err,
                "studentProfile:saveLanguage"
              );
              return of(false as const);
            })
          )
          .subscribe((result) => {
            if (result !== false) {
              this.alertService.success(alertMessages.successDataSaved);
              this.activeModal.close(true);
            }
          });
      } else {
        const { id, ...body } = this.entity;
        this.studentProfileService
          .createLanguageProficiency({ profileId: this.profile_id, body })
          .pipe(
            catchError((err) => {
              this.alertService.errorWithReport(
                err,
                "studentProfile:saveLanguage"
              );
              return of(false as const);
            })
          )
          .subscribe((result) => {
            if (result !== false) {
              this.alertService.success(alertMessages.successDataSaved);
              this.activeModal.close(true);
            }
          });
      }
    } else {
      this.validationService.goToModalError();
    }
  }
}
